import { ValueGetterParams } from 'ag-grid-enterprise';
import { ExtendedColDef } from 'interfaces/table.interface';
import AgGridDateFilter from '../../components/DashboardStartEndTime/Aggriddatefilter';
import AgGridDurationFilter from '../../components/DurationFilter/AgGridDurationFilter';
import {
  MAP_STATUS_ICON,
  ORCHESTRATION_FILTERS,
  ORCHESTRATION_STATUSES,
  TABLE_FIELDS,
} from '../../utils/common-constants';
import { IOrchestrationStatus } from '../../interfaces/dashboard/orchestrationStatus.interface';
import {
  defaultFilterParams,
  timeFilterOptions,
} from 'components/TableContainer/utils/filterUtils';
import { DateUtils } from 'utils/dateUtils/DateUtils';

export const TABLE_SCROLL_SELECTOR = '.wk-advanced-table';
export const DEFAULT_COLUMN_HEIGHT = 40;

export const ORCHESTRATION_FILTER_ITEMS: IOrchestrationStatus[] = [
  {
    id: 'total',
    filterId: ORCHESTRATION_FILTERS.ANY,
    title: 'Total',
    description: 'Orchestrations',
    iconClass: '',
    count: 0,
  },
  {
    id: 'pending',
    filterId: ORCHESTRATION_FILTERS.PENDING,
    title: 'Pending',
    description: 'Not started yet',
    iconClass: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.PENDING].name,
    iconColor: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.PENDING].color,
    iconSize: 24,
    count: 0,
  },
  {
    id: 'inProgress',
    filterId: ORCHESTRATION_FILTERS.IN_PROGRESS,
    title: 'In Progress',
    description: 'Running',
    iconClass: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.IN_PROGRESS].name,
    iconColor: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.IN_PROGRESS].color,
    iconSize: 20,
    count: 0,
  },
  {
    id: 'failed',
    filterId: ORCHESTRATION_FILTERS.FAILED,
    title: 'Failed',
    description: 'With errors',
    iconClass: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.FAILED].name,
    iconColor: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.FAILED].color,
    iconSize: 20,
    count: 0,
  },
  {
    id: 'stopped',
    filterId: ORCHESTRATION_FILTERS.STOPPED,
    title: 'Stopped',
    description: 'Stopped by',
    iconClass: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.STOPPED].name,
    iconColor: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.STOPPED].color,
    iconSize: 20,
    count: 0,
  },
  {
    id: 'finished',
    filterId: ORCHESTRATION_FILTERS.FINISHED,
    title: 'Finished',
    description: 'Succeeded',
    iconClass: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.FINISHED].name,
    iconColor: MAP_STATUS_ICON[ORCHESTRATION_STATUSES.FINISHED].color,
    iconSize: 20,
    count: 0,
  },
];

export const DEFAULT_FILTER_MENU_ICON = '<i class="ag-icon ag-icon-menu"/>';

export const TABLE_COLUMNS: ExtendedColDef[] = [
  {
    headerName: 'ID',
    field: TABLE_FIELDS.id,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    menuTabs: ['filterMenuTab'],
    filter: 'agNumberColumnFilter',
    cellRenderer: 'cellMaster',
    filterParams: {
      filterOptions: ['equals'],
      ...defaultFilterParams,
    },
    icons: {
      menu: DEFAULT_FILTER_MENU_ICON,
    },
    minWidth: 130,
    width: 130,
    maxWidth: 130,
    sortable: false,
    cellStyle: {
      paddingLeft: '18px',
    },
    headerClass: 'jobId-custom-class',
  },
  {
    headerName: 'Orchestration Name/Product Variant',
    field: TABLE_FIELDS.name,
    menuTabs: ['filterMenuTab'],
    filter: 'agSetColumnFilter',
    cellRenderer: 'cellOrchestrationName',
    filterParams: {
      defaultToNothingSelected: true,
      ...defaultFilterParams,
    },
    icons: {
      menu: DEFAULT_FILTER_MENU_ICON,
    },
    minWidth: 400,
    width: 400,
    comparator: () => 0,
  },
  {
    headerName: 'Input File',
    field: TABLE_FIELDS.originalFileName,
    menuTabs: ['filterMenuTab'],
    filter: 'agTextColumnFilter',
    cellRenderer: 'cellInputField',
    filterParams: {
      defaultToNothingSelected: true,
      filterOptions: ['contains'],
      filterPlaceholder: 'Search...',
      ...defaultFilterParams,
    },
    icons: {
      menu: DEFAULT_FILTER_MENU_ICON,
    },
    minWidth: 150,
    width: 150,
    sortable: false,
  },
  {
    headerName: 'Start Time',
    field: TABLE_FIELDS.startTime,
    filter: AgGridDateFilter,
    menuTabs: ['filterMenuTab'],
    valueFormatter: ({ value }) => DateUtils.formatUtcToLocalTimezone(value),
    filterParams: {
      filterOptions: timeFilterOptions,
      defaultOption: 'greaterThan',
      ...defaultFilterParams,
    },
    minWidth: 155,
    width: 155,
    maxWidth: 155,
    comparator: () => 0,
  },
  {
    headerName: 'End Time',
    field: TABLE_FIELDS.endTime,
    filter: AgGridDateFilter,
    menuTabs: ['filterMenuTab'],
    valueFormatter: ({ value }) => DateUtils.formatUtcToLocalTimezone(value),
    filterParams: {
      filterOptions: timeFilterOptions,
      defaultOption: 'lessThan',
      ...defaultFilterParams,
    },
    minWidth: 155,
    width: 155,
    maxWidth: 155,
    comparator: () => 0,
  },
  {
    headerName: 'Duration',
    field: TABLE_FIELDS.duration,
    menuTabs: ['filterMenuTab'],
    filter: AgGridDurationFilter,
    cellRenderer: 'cellDuration',
    valueGetter: (params: ValueGetterParams<any>) => {
      const d = params.data || {};
      // Force refresh solo cuando cambian status/start/end; evitamos depender de duration
      return `${d.status}|${d.startTime}|${d.endTime}`;
    },
    filterParams: {
      defaultOption: 'lessThan',
      ...defaultFilterParams,
    },
    minWidth: 120,
    width: 120,
    maxWidth: 120,
    comparator: () => 0,
  },
  {
    headerName: 'Status',
    field: TABLE_FIELDS.status,
    cellRenderer: 'cellStatus',
    cellClass: 'csb-visible-cell',
    minWidth: 57,
    width: 57,
    maxWidth: 57,
    menuTabs: [],
    sortable: false,
    cellStyle: {
      justifyContent: 'center',
    },
  },
  {
    headerName: 'Log Data',
    field: TABLE_FIELDS.status,
    cellRenderer: 'cellLogData',
    cellClass: 'csb-visible-cell',
    minWidth: 79,
    width: 79,
    maxWidth: 79,
    menuTabs: [],
    sortable: false,
    cellStyle: {
      justifyContent: 'center',
    },
  },
  {
    headerName: 'Job Started By',
    field: TABLE_FIELDS.user,
    minWidth: 155,
    width: 155,
    filter: 'agSetColumnFilter',
    menuTabs: ['filterMenuTab'],
    filterParams: {
      defaultToNothingSelected: true,
      ...defaultFilterParams,
    },
    icons: {
      menu: DEFAULT_FILTER_MENU_ICON,
    },
    comparator: () => 0,
    filterValueGetter: (params: ValueGetterParams<any>) =>
      params.data.user.name,
  },
  {
    headerName: 'Step',
    field: TABLE_FIELDS.step,
    cellRenderer: 'cellWithTooltip',
    cellClass: 'csb-visible-tooltip',
    minWidth: 140,
    width: 140,
    maxWidth: 140,
    menuTabs: [],
    sortable: false,
  },
];
